import Preview from "./preview";

import {
    alignBelowAnnotations,
    mergeAnnotations,
    linearDomain,
    attributeAnnotations,
    dnaClassColor,
    scaleAnnotationSize,
} from "./utils";

import {
    severityToColor
} from "../mixins/dictionaries";

import {
    annotationPath
} from "../mixins/linearShape";

import Brush from "../mixins/linearBrush";

export default class LinearPreview extends Preview {
    constructor(settings, mediator) {
        super(settings, mediator)

        this.EventsOn();

        this.initContainer();
        this.brush = new Brush();

        this.brush.init(this.previewBoxContainer, this.$previewBoxContainer);
        this.brush.create();
    }

    initData(data) {
        super.initDataSequence(data)
        this.domainX = linearDomain([0, this.sequenceLength], this.$previewBoxContainer.width);

        this.setComponentPositions();
        this.initPosition = 0;
    }

    initContainer() {
        super.initContainer("#previewEditor", {height: "120px", width: "100%"});

        this.addEventsContainer();
    }

    setComponentPositions() {
        const offsetRuler = 13;
        const offsetSlot = 5;
        const basePairHeight = 30;
        this.positionsY = {
            ruler: this.initYPosition + offsetRuler,
            slots: this.initYPosition + offsetSlot,
            restrictionEnzymes: this.initYPosition + offsetRuler,
            annotations: {
                above: this.initYPosition,
                below: this.initYPosition + basePairHeight
            },
        };

        this.previewBoxContainer
            .attr("transform", "translate(0,0)");

        this.regularFontSizeWidth = this.$previewBoxContainer.width / 180;
        this.fontBoxWidth = (this.$previewBoxContainer.width / this.sequenceLength);
    }

    setData(data) {
        this.initData(data)
        this.render();
    }

    render() {

        this.drawSlots();
        this.drawRuler();
        this.drawAnnotations();
    }

    drawSlots() {
        const slotHeight = 5;
        let start = 0;
        let end = 0;

        this.previewBoxContainer.selectAll(".slot").remove();

        this.srcData.slots.forEach(s => {
            end = start + s.length;
            if (s.length) {
                this.drawSlot(this.previewBoxContainer, s, start, end, slotHeight);
            }
            start = end;
        })
    }

    drawSlot(container, s, slotStart, slotEnd, height) {
        const [start, end, arrowLength, direction] = scaleAnnotationSize(slotStart, slotEnd, s.direction, 6, this.fontBoxWidth, this.regularFontSizeWidth, this.sequenceLength);

        container
            .append("path")
            .attr("class", "slot")
            .attr("d", annotationPath(this.positionsY.slots, height, start, end, s.direction, arrowLength, this.fontBoxWidth))
            .attr("height", height)
            .style("stroke", "#000")
            .style("stroke-width", .2)
            .style("opacity", 1);

        // edge
        container
            .append("line")
            .attr("class", "slot")
            .attr("x1", this.domainX(slotStart))
            .attr("y1", 0)
            .attr("x2", this.domainX(slotStart))
            .attr("y2", "100%")
            .style("stroke-dasharray", ("3, 3"));

        // is editable, is public
        if (dnaClassColor(s.dna_sequence)) {
            container.append("line")
                .attr("class", "slot " + dnaClassColor(s.dna_sequence))
                .attr("x1", this.domainX(start))
                .attr("y1", this.positionsY.slots + height + 1)
                .attr("x2", this.domainX(end))
                .attr("y2", this.positionsY.slots + height + 1)
                .attr('stroke-width', 1);
        }
    }

    drawRuler() {
        const ticksSlices = this.sequenceLength > 10 ? 10 : this.sequenceLength

        this.previewBoxContainer.selectAll(".ruler").remove();

        const rulerContainer = this.previewBoxContainer
            .append("g")
            .attr("class", "ruler")
            .attr("transform", "translate(0," + this.positionsY.ruler + ")")

        rulerContainer.append("line")
            .attr("x1", 0)
            .attr("y1", 0)
            .attr("x2", "100%")
            .attr("y2", 0)
            .style("stroke", "#000")
            .style("stroke-width", .5);


        const ticks = this.domainX.ticks(ticksSlices)
        const tickHeight = 8;
        ticks.forEach(t => {
            if (t) {
                this.drawTick(rulerContainer, t, tickHeight)
            }
        })
    }

    drawTick(container, tick, height) {
        const tickTextPosition = height + 8;
        container.append("line")
            .attr("x1", this.domainX(tick))
            .attr("y1", 0)
            .attr("x2", this.domainX(tick))
            .attr("y2", height)
            .style("stroke", "#000")
            .style("stroke-width", "1")
            .style("opacity", "1")

        container
            .append("text")
            .attr("class", "tick")
            .attr("x", this.domainX(tick))
            .attr("dy", tickTextPosition)
            .style("font-size", .7 + "em")
            .style("text-anchor", "middle")
            .style('fill', "#000")
            .text(tick);
    }

    updateAnnotations(data) {
        this.maxBelowLevel = -1;
        this.maxAboveLevel = -1;

        [this.annotations, this.maxBelowLevel, this.maxAboveLevel] = alignBelowAnnotations(mergeAnnotations(data, this.previewAnnotations, this.protocolAnnotations, this.sequenceLength, this.hiddenAnnotations));
        this.drawAnnotations();
    }

    drawAnnotations() {
        const paddingTop = 2;
        const offsetYAnnotations = this.positionsY.annotations.below + this.positionsY.annotations.above + paddingTop;
        const stepLevel = 2;
        this.maxBelowLevel += 1;
        this.maxAboveLevel += 1;
        const annotationHeight = Math.min(6, (this.$previewBoxContainer.height - offsetYAnnotations - ((stepLevel * (this.maxBelowLevel + this.maxAboveLevel))) / (this.maxBelowLevel + this.maxAboveLevel)));

        let layer = 0;

        this.previewBoxContainer.selectAll(".annotation").remove();

        this.annotations.forEach(a => {
            layer = (stepLevel + annotationHeight) * a.level;
            if (a.placement === "above") {
                layer = this.positionsY.annotations.above - layer;
            } else {
                layer = this.positionsY.annotations.below + layer;
            }
            this.drawAnnotation(this.previewBoxContainer, a, annotationHeight, layer);
        });
    }

    drawAnnotation(container, a, height, layer) {
        const [start, end, arrowLength, direction] = scaleAnnotationSize(a.start, a.end, a.direction, 6, this.fontBoxWidth, this.regularFontSizeWidth, this.sequenceLength);
        const attributeAnnotation = attributeAnnotations(a.role_id, this.settings.annotation_colors);
        const annotationPathContainer = annotationPath(layer, height, start, end, direction, arrowLength, this.fontBoxWidth)
        container
            .append("path")
            .attr("class", "annotation annotation_" + a.pk)
            .attr("d", annotationPathContainer)
            .attr("fill", attributeAnnotation.color)
            .style("stroke", "#000")
            .style("stroke-width", 0)
            .style("opacity", a.visible ? .6 : 0)

        if (a.error_messages && a.error_messages.length === 1) {
            container.append("path")
                .attr("class", "annotation annotation_" + a.pk)
                .attr("d", annotationPathContainer)
                .attr("fill", attributeAnnotation.color)
                .style("stroke", severityToColor[a.error_messages[0].severity] )
                .style("stroke-width", .7)
                .style("opacity", a.visible ? 1 : 0)
        }
    }

    renderRestrictionEnzymes(data) {
        this.restrictionEnzymes = data;
        const initHeight = this.srcData.slots && this.srcData.slots.length ? 7 : 4;
        const restEnzymeHeight = 4;
        let height = 0;

        this.previewBoxContainer.selectAll(".restEnzymes").remove();

        const restEnzymeContainer = this.previewBoxContainer
            .append("g")
            .attr("class", "restEnzymes")
            .attr("transform", "translate(0," + this.positionsY.restrictionEnzymes + ")")

        this.restrictionEnzymes.forEach(r => {
            height = initHeight + ((r.level + 1) * restEnzymeHeight);
            this.drawRestrictionEnzyme(restEnzymeContainer, r, height)
        })
    }


    drawRestrictionEnzyme(container, re, height) {
        container
            .append("line")
            .attr("class", "restEnzyme restEnzyme_" + re.id)
            .attr("x1", this.domainX(re.cut_5))
            .attr("y1", 0)
            .attr("x2", this.domainX(re.cut_5))
            .attr("y2", -height)
            .attr("stroke", "#000")
            .attr("stroke-width", .5)
            .style("opacity", re.visible ? 1 : 0)
    }

    addEventsContainer() {
        this.previewBox
            .on("contextmenu", (e) => {
                e.preventDefault();
                e.stopPropagation();
            })
            .on("click", (e) => {
                e.preventDefault();
                e.stopPropagation();
                const minEdgeOffsetScroll = 0
                const maxEdgeOffsetScroll = this.sequenceLength - this.domainX.invert(this.brushSize.width);
                this.offsetScroll.current = Math.round(this.domainX.invert(e.offsetX - (this.brushSize.width / 2)))

                if (this.offsetScroll.current < minEdgeOffsetScroll || this.offsetScroll.current > maxEdgeOffsetScroll) {
                    if (this.offsetScroll.current < minEdgeOffsetScroll) {
                        this.offsetScroll.current = minEdgeOffsetScroll
                    } else {
                        this.offsetScroll.current = maxEdgeOffsetScroll
                    }
                }

                this.EventsTrigger("hideContextMenu");
                this.EventsTrigger("setOffsetScroll", this.offsetScroll.current)
                this.EventsTrigger("slide", "end")

                this.slide();
            })
    }

    setBrushSize(data) {
        this.offsetScroll.current = data.position
        this.brushSize = {
            x: this.domainX(this.offsetScroll.current),
            width: this.domainX(data.width)
        }

        this.brush.setParameters(this.brushSize)
    }

    setOffsetScroll(data) {
        this.offsetScroll.current = data;

        this.brush.setParameters({
            x: this.domainX(this.offsetScroll.current),
            width: this.brushSize.width
        })
    }

    setScroll(scroll) {
        const min = 0
        const max = this.sequenceLength - this.domainX.invert(this.brushSize.width);

        this.offsetScroll.current += scroll * -1;

        if (this.offsetScroll.current < min || this.offsetScroll.current > max) {
            if (this.offsetScroll.current < min) {
                this.offsetScroll.current = min
            } else {
                this.offsetScroll.current = max
            }
        }
    }

    slide() {
        this.brush.setParameters({
            x: this.domainX(this.offsetScroll.current),
            width: this.brushSize.width
        })
    }

    toggleItems(data) {
        if (data.selector === "restrictionEnzymes") {
            this.restrictionEnzymes.forEach(el => {
                if (data.objects.includes(el.id)) {
                    if (!data.visible) {
                        this.hiddenRestrictionEnzymes.push(el.id)
                    } else {
                        this.hiddenRestrictionEnzymes = this.hiddenRestrictionEnzymes.filter(item => item !== el.id)
                    }
                    el.visible = data.visible;

                    this.previewBoxContainer.selectAll(".restEnzyme_" + el.id)
                        .style("opacity", data.visible ? 1 : 0);
                }
            })
        } else {
            this.annotations.forEach(el => {
                if (data.objects.includes(el.pk)) {
                    if (!data.visible) {
                        this.hiddenAnnotations.push(el.pk)
                    } else {
                        this.hiddenAnnotations = this.hiddenAnnotations.filter(item => item !== el.pk)
                    }

                    el.visible = data.visible;
                    this.previewBoxContainer.selectAll(".annotation_" + el.pk)
                        .style("opacity", data.visible ? .6 : 0);
                }
            })
        }
    }

    EventsOn() {
        super.EventsOn();
        if (this.mediator) {
            this.mediator.on("setData", "setData", this);
            this.mediator.on("setAreaView", "setBrushSize", this);
            this.mediator.on("setOffsetScroll", "setOffsetScroll", this);
            this.mediator.on("setScroll", "setScroll", this);
            this.mediator.on("slide", "slide", this);

            this.mediator.on("updateAnnotations", "updateAnnotations", this);
            this.mediator.on("loadRestrictionEnzymes", "renderRestrictionEnzymes", this);

            this.mediator.on("toggleObjects", "toggleItems", this);
        }
    }
}